import { cartApi, toursApi } from "@api";
import {
    BOOKING_DATE_FORMAT,
    BOOKING_SYSTEM_DEFAULT_BOOKING_TIME_ID,
    BOOKING_SYSTEM_IBIS,
    BOOKING_SYSTEM_RESPAX_V2,
    BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS,
} from "@constants";
import {
    BookingData,
    CheckoutType,
    ICheckoutContext,
    IAvailabilityByProductAndRange,
    IAvailabilityByRange,
    IAvailField,
    IBookingAvailability,
    IBookingCheckInOptions,
    IBookingDropOffLocation,
    IBookingPickUpLocation,
    IBookingPickUpTime,
    ICartItemData,
    ICartItemFareData,
    ICartItemsByGroup,
    ICartItemsByProduct,
    IPriceInfo,
    ITotalNetRateInfo,
    ITotalRRPInfo,
    ITourFareBookingTime,
    IUpdateCartItemData,
    IAddItemBase,
    IOptionalField,
} from "@interfaces";
import { useWindowDimensions } from "@utilities";
import _ from "lodash";
import moment from "moment";
import { quoteApi } from "src/Api/quoteApi";
import { getCartItemFareRRP } from "./fareRRP";

export const buildCartCheckoutContext = (): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.CART,
    };
};

export const buildQuoteCheckoutContext = (quoteId: string): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.QUOTE,
        quoteId,
    };
};

export const buildBookNowCheckoutContext = (): ICheckoutContext => {
    return {
        checkoutType: CheckoutType.BOOK_NOW,
    };
};

export const isMobileViewHandler = () => {
    const { width } = useWindowDimensions();
    return width < 768;
};

export const CheckoutCartStepLabels: string[] = ["Cart", "Checkout"];
export const QuoteStepLabels: string[] = ["Items", "Checkout"];
export const BookNowStepLabels: string[] = ["Cart", "Checkout"];

export const isAvailabiltyByRange = (availability: IBookingAvailability): boolean => {
    return "productPricingData" in availability;
};

export const getCartItemFareData = (cartItem: ICartItemData): ICartItemFareData => {
    if (!cartItem.product?.json?.faresprices) {
        console.error("Cart item has no product data (may be expired or unavailable):", cartItem);
        return { id: "", name: "", bookingSystem: "" };
    }

    const farePrice = isAvailabiltyByRange(cartItem.availability)
        ? cartItem.product.json.faresprices.find(
              (fare) =>
                  fare.productPricesDetailsId.toString() ===
                  cartItem.product_price_details_id.toString(),
          )
        : cartItem.product.json.faresprices.find(
              (fare) =>
                  fare.productPricesDetailsId.toString() ===
                      cartItem.product_price_details_id.toString() ||
                  fare.fareTypeId.toString() ===
                      (
                          cartItem.availability as IAvailabilityByProductAndRange
                      ).FarePrice?.fareTypeId?.toString(),
          );

    if (!farePrice) {
        console.error("farePrice not found for cartItem, the item has expired:", cartItem);
        /* 
        TODO:Temporary fallback for now, need to handle later with disabling items.
        Only the expired items do not have fariPrice in the data. Which was casuing the cart page to show black screen.
        */
        return { id: "", name: "", bookingSystem: "" };
    }

    return {
        id: farePrice.fareTypeId.toString(),
        name: farePrice.fareName,
        bookingSystem: farePrice.bookingSystem,
    };
};

export const getCartItemUnitRRP = (cartItem: ICartItemData): number | null => {
    const structuredRRP = isAvailabiltyByRange(cartItem.availability)
        ? (cartItem.availability as IAvailabilityByRange).productPricingData?.RRP
        : (cartItem.availability as IAvailabilityByProductAndRange).FarePrice?.RRP;
    if (!_.isNil(structuredRRP)) return structuredRRP;
    // Combined-price-rule fares (and any item still awaiting the async
    // availability check) come back with `pendingAvailabilityCheck: true` and
    // omit both productPricingData and FarePrice — the unit RRP is carried only
    // in `totalPrice` (a unit price; it does NOT scale with quantity). Without
    // this fallback such items priced at 0.00 in the cart line and totals.
    const pendingUnitRRP = (cartItem.availability as { totalPrice?: number | null }).totalPrice;
    if (!_.isNil(pendingUnitRRP)) return pendingUnitRRP;
    // Last resort: the fare's cached list RRP from `product.json.faresprices`.
    // When the live availability check fails server-side (e.g. TDMS "Failed to
    // load booking details" for a CPR/Generic fare), the stored item carries NO
    // price in `availability.*` at all — but the cached product still holds the
    // fare's RRP. Prefer live availability pricing above; only fall back here.
    return getCartItemFareRRP(cartItem);
};

export const getCartItemRRP = (cartItem: ICartItemData): number => {
    const unitRRP = getCartItemUnitRRP(cartItem);
    return (unitRRP ?? 0) * cartItem.booking_quantity;
};

export const getCartItemUnitNetRate = (cartItem: ICartItemData): number | null => {
    return isAvailabiltyByRange(cartItem.availability)
        ? (cartItem.availability as IAvailabilityByRange).productPricingData.NetRate
        : ((cartItem.availability as IAvailabilityByProductAndRange).FarePrice?.NetRate ?? null);
};

export const getCartItemNetRate = (cartItem: ICartItemData): number => {
    const unitNetRate = getCartItemUnitNetRate(cartItem);
    return (unitNetRate ?? 0) * cartItem.booking_quantity;
};

export const getTotalRRP = (cartItems: ICartItemData[]): ITotalRRPInfo => {
    return {
        totalRRP: cartItems.reduce((total, cartItem) => {
            return total + getCartItemRRP(cartItem);
        }, 0),
        hasMissingPrices: cartItems.some((cartItem) => {
            return getCartItemUnitRRP(cartItem) === null;
        }),
    };
};

export const getTotalNetRate = (cartItems: ICartItemData[]): ITotalNetRateInfo => {
    return {
        totalNetRate: cartItems.reduce((total, cartItem) => {
            return total + getCartItemNetRate(cartItem);
        }, 0),
        hasMissingPrices: cartItems.some((cartItem) => {
            return getCartItemUnitNetRate(cartItem) === null;
        }),
    };
};

export const getItemsPriceInfo = (cartItems: ICartItemData[]): IPriceInfo => {
    const rrpInfo = getTotalRRP(cartItems);
    const netRateInfo = getTotalNetRate(cartItems);

    const commission = rrpInfo.totalRRP - netRateInfo.totalNetRate;
    const commissionPercent = rrpInfo.totalRRP ? (commission / rrpInfo.totalRRP) * 100 : 0;

    return {
        totalRRP: rrpInfo.totalRRP,
        totalNetRate: netRateInfo.totalNetRate,
        commission,
        commissionPercent,
        totalPrice: rrpInfo.totalRRP,
        hasMissingPrices: rrpInfo.hasMissingPrices || netRateInfo.hasMissingPrices,
    };
};

export const groupCartItemsByGroup = (cartItems: ICartItemData[]): ICartItemsByGroup[] => {
    const ret = cartItems.reduce((acc: ICartItemsByGroup[], cartItem) => {
        const group = acc.find((group) => group.group_id === cartItem.group_id);
        if (group) {
            group.items.push(cartItem);
        } else {
            acc.push({
                group_id: cartItem.group_id ?? null,
                items: [cartItem],
            });
        }

        return acc;
    }, []);
    return ret;
};

export const groupCartItemsByProduct = (cartItems: ICartItemData[]): ICartItemsByProduct[] => {
    return cartItems.reduce((acc: ICartItemsByProduct[], cartItem) => {
        if (!cartItem.product?.json) {
            return acc;
        }
        const product = acc.find(
            (product) => product.productId.toString() === cartItem.tdms_product_id.toString(),
        );
        const fareData = getCartItemFareData(cartItem);
        if (product) {
            const existingItemsWithThisFare = product.itemsByFare.find(
                (fare) => fare.id === fareData.id,
            );
            if (existingItemsWithThisFare) {
                // For Accommodation (class 'A'), we need to group items by group_id
                // For other product classes, always create a new group
                if (product.productClass === "A") {
                    const group = existingItemsWithThisFare.itemsByGroup.find(
                        (group) => group.group_id === cartItem.group_id,
                    );
                    if (group) {
                        group.items.push(cartItem);
                    } else {
                        existingItemsWithThisFare.itemsByGroup.push({
                            group_id: cartItem.group_id ?? null,
                            items: [cartItem],
                        });
                    }
                } else {
                    // For non-Accommodation products, always create a new group
                    existingItemsWithThisFare.itemsByGroup.push({
                        group_id: cartItem.group_id ?? null,
                        items: [cartItem],
                    });
                }
            } else {
                product.itemsByFare.push({
                    ...fareData,
                    itemsByGroup: [
                        {
                            group_id: cartItem.group_id ?? null,
                            items: [cartItem],
                        },
                    ],
                });
            }
        } else {
            const fareData = getCartItemFareData(cartItem);
            acc.push({
                productId: cartItem.tdms_product_id.toString(),
                productClass: cartItem.product.json.productClass,
                name: cartItem.product.json.name,
                productImagePath: cartItem.product.json.productImagePath,
                supplierName: cartItem.product.json.supplierName,
                startLocationName: cartItem.product.json.categories.startLocationName,
                itemsByFare: [
                    {
                        ...fareData,
                        itemsByGroup: [
                            {
                                group_id: cartItem.group_id ?? null,
                                items: [cartItem],
                            },
                        ],
                    },
                ],
            });
        }
        return acc;
    }, []);
};

export const formatBookingDatesByProductClass = (
    bookingDates: string[],
    productClass: string,
): string => {
    if (!bookingDates?.length) return "";

    const sortedDates = bookingDates
        .map((date) => moment(date, ["YYYY-MM-DD", "DD-MMM-YYYY"]))
        .sort((a, b) => a.valueOf() - b.valueOf());

    // For Tours (Y)
    if (productClass === "Y") {
        return `Starts on ${sortedDates.map((date) => date.format("MMM D")).join(", ")}`;
    }

    // For Activities (V)
    if (productClass === "V") {
        return sortedDates.map((date) => date.format("MMM D")).join(", ");
    }

    // For Accommodation (A)
    if (productClass === "A") {
        const ranges: string[] = [];
        let rangeStart = sortedDates[0];
        let prevDate = sortedDates[0];

        for (let i = 1; i <= sortedDates.length; i++) {
            const currentDate = sortedDates[i];

            // Check if dates are not consecutive or if we've reached the end
            if (!currentDate || currentDate.diff(prevDate, "days") > 1) {
                // Add one day to the last date for checkout
                const rangeEnd = prevDate.clone().add(1, "day");

                // Format the range
                ranges.push(
                    rangeStart.isSame(prevDate)
                        ? `${rangeStart.format("MMM D")} - ${rangeEnd.format("MMM D")}`
                        : `${rangeStart.format("MMM D")} - ${rangeEnd.format("MMM D")}`,
                );

                // Start new range if there are more dates
                if (currentDate) {
                    rangeStart = currentDate;
                }
            }

            if (currentDate) {
                prevDate = currentDate;
            }
        }

        return ranges.join(", ");
    }

    return "";
};

export const getCheckInDateItem = (items: ICartItemData[]) =>
    items
        .sort(
            (a, b) =>
                moment(a.booking_date, BOOKING_DATE_FORMAT).valueOf() -
                moment(b.booking_date, BOOKING_DATE_FORMAT).valueOf(),
        )
        .at(0);

export const getCheckInAvail = (avails: IBookingAvailability[]) =>
    avails
        .sort(
            (a, b) =>
                moment(a.BookingDate, BOOKING_DATE_FORMAT).valueOf() -
                moment(b.BookingDate, BOOKING_DATE_FORMAT).valueOf(),
        )
        .at(0);

export const checkBookingNeedsPickUpTimeID = (bookingSystem: string) =>
    BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS.includes(bookingSystem.toLowerCase());

export const getNumPax = (item: ICartItemData): number => {
    if (isAvailabiltyByRange(item.availability)) {
        const found = item.product.json.faresprices.find(
            (fp) => Number(fp.productPricesDetailsId) === Number(item.product_price_details_id),
        );
        return Number(found?.numPax ?? "1");
    } else {
        const fareTypeId = (item.availability as IAvailabilityByProductAndRange).FarePrice
            ?.fareTypeId;
        const found = item.product.json.faresprices.find(
            (fp) => Number(fp.productPricesDetailsId) === Number(fareTypeId),
        );
        return Number(found?.numPax ?? "1");
    }
};

export const getAvailsCheckInOptions = async (
    bookingSystem: string,
    checkInAvail: IBookingAvailability,
    checkInBookingDetails: ITourFareBookingTime,
) => {
    if (bookingSystem.toLowerCase() === BOOKING_SYSTEM_RESPAX_V2) {
        let availabilityByRange = checkInAvail as IAvailabilityByRange;

        const hasTimeIdFields = (availabilityByRange.fields ?? []).some(
            (f) => f.name.trim().toLowerCase() === "timeid",
        );

        if (!hasTimeIdFields) {
            try {
                const refetched = await toursApi.checkAvailRange(
                    availabilityByRange.productPricesDetailsId.toString(),
                    BOOKING_SYSTEM_DEFAULT_BOOKING_TIME_ID,
                    new Date(checkInAvail.BookingDate),
                    1,
                );
                const matchingAvail = refetched.find(
                    (a) => a.BookingDate === checkInAvail.BookingDate,
                );
                if (matchingAvail && (matchingAvail.fields ?? []).some((f) => f.name.trim().toLowerCase() === "timeid")) {
                    availabilityByRange = matchingAvail as IAvailabilityByRange;
                }
            } catch {
            }
        }

        const respaxPickUpTimeInfos: IBookingPickUpTime[] = (availabilityByRange.fields ?? [])
            .filter((bt) => bt.name.trim().toLowerCase() === "timeid")
            .map((bt) => ({
                displayName: bt.displayName,
                options: bt.options.map((o) => ({
                    id: o.externalId.toString(),
                    displayName: o.displayName,
                    isDefault: false,
                    downstreamTimeRef: o.externalId.toString(),
                    numAvailable: o.numAvailable,
                })),
            }));
        const uniqueTimeOptions = _.uniqBy(
            respaxPickUpTimeInfos.flatMap((i) => i.options),
            (opt) => opt.id,
        );
        const respaxDetailsToConfirmReqs = uniqueTimeOptions.map((bookingTime) =>
            cartApi
                .confirmAvailability(
                    availabilityByRange.productPricesDetailsId.toString(),
                    checkInAvail.BookingDate,
                    bookingTime.id,
                )
                .then((resp) => ({
                    timeId: bookingTime.id,
                    fields: resp.data.fields,
                })),
        );
        const respaxDetailsToConfirmResps = await Promise.allSettled(respaxDetailsToConfirmReqs);
        const fieldsWithTimeID = respaxDetailsToConfirmResps
            .filter(
                (p): p is PromiseFulfilledResult<{ timeId: string; fields: IAvailField[] }> =>
                    p.status === "fulfilled",
            )
            .map((p) => p.value)
            .flat();
        const respaxPickupLocationInfos: IBookingPickUpLocation[] = fieldsWithTimeID
            .map((f) => ({
                ...f,
                fields: f.fields.filter((f: any) => f.name.toLowerCase() === "pickupid"),
            }))
            .flatMap((f) =>
                f.fields.map((ff: IAvailField) => ({
                    displayName: ff.displayName,
                    upstreamTimeRef: f.timeId,
                    options: ff.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                })),
            );

        const respaxDropOffLocationInfos: IBookingDropOffLocation[] = fieldsWithTimeID
            .map((f) => ({
                ...f,
                fields: f.fields.filter((f: IAvailField) => f.name.toLowerCase() === "dropoffid"),
            }))
            .flatMap((f) =>
                f.fields.map((ff: IAvailField) => ({
                    displayName: ff.displayName,
                    upstreamTimeRef: f.timeId,
                    options: ff.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                })),
            );
        return {
            pickupTimes: respaxPickUpTimeInfos,
            pickupLocations: respaxPickupLocationInfos,
            dropoffLocations: respaxDropOffLocationInfos,
        };
    } else if (bookingSystem.toLowerCase() === BOOKING_SYSTEM_IBIS) {
        const bookingTimeRef = BOOKING_SYSTEM_DEFAULT_BOOKING_TIME_ID;
        const ppdid = isAvailabiltyByRange(checkInAvail)
            ? (checkInAvail as IAvailabilityByRange).productPricesDetailsId.toString()
            : (
                  checkInAvail as IAvailabilityByProductAndRange
              ).FarePrice?.productPricesDetailsId.toString();

        if (_.isNil(ppdid)) {
            return null;
        }

        const ibisDetailsToConfirmReqs = await cartApi.confirmAvailability(
            ppdid,
            checkInAvail.BookingDate,
        );
        const ibisPickUpTimeInfos: IBookingPickUpTime[] = ibisDetailsToConfirmReqs.data.fields
            .filter((f) => f.name.toLowerCase() === "timeid")
            .map((f) => ({
                displayName: f.displayName,
                options: f.options.map((o) => ({
                    id: o.externalId.toString(),
                    displayName: o.displayName,
                    isDefault: false,
                    downstreamTimeRef: bookingTimeRef,
                    numAvailable: o.numAvailable,
                })),
            }));
        const ibisPickUpLocationInfos: IBookingPickUpLocation[] =
            ibisDetailsToConfirmReqs.data.fields
                .filter((f) => f.name.toLowerCase() === "pickupid")
                .map((f) => ({
                    displayName: f.displayName,
                    upstreamTimeRef: bookingTimeRef,
                    options: f.options.map((o) => ({
                        id: o.externalId.toString(),
                        displayName: o.displayName,
                    })),
                }));
        return {
            pickupTimes: ibisPickUpTimeInfos,
            pickupLocations: ibisPickUpLocationInfos,
            dropoffLocations: [],
        };
    } else {
        const genericPickUpTimInfo: IBookingPickUpTime = {
            displayName: "Booking Time",
            options: checkInBookingDetails.bookingTimes.map((bt) => ({
                id: bt.BookingTimeID.toString(),
                displayName: bt.BookingTime,
                isDefault: bt.Default,
                downstreamTimeRef: null,
            })),
        };

        const genericPickUpLocationInfo: IBookingPickUpLocation = {
            displayName: "Pickup",
            upstreamTimeRef: null,
            options: checkInBookingDetails.pickupLocations.map((pl) => ({
                id: pl.PickupID!.toString(),
                displayName: `${pl.PickupTime} ${pl.PickupLocation}`,
            })),
        };

        return {
            pickupTimes: [genericPickUpTimInfo],
            pickupLocations: [genericPickUpLocationInfo],
            dropoffLocations: [],
        };
    }
};

export const getBookingCheckInOptions = async (
    checkInDateItem: ICartItemData,
): Promise<IBookingCheckInOptions | null> => {
    const farePrice = getCartItemFareData(checkInDateItem);
    const checkInOptions = await getAvailsCheckInOptions(
        farePrice.bookingSystem,
        checkInDateItem.availability,
        checkInDateItem.booking_details,
    );
    if (_.isNil(checkInOptions)) return null;
    return {
        fareTypeId: farePrice.id,
        productPricesDetailsId: checkInDateItem.product_price_details_id.toString(),
        ...checkInOptions,
    };
};

// validate that all items have same number of booking_data and booking_quantity
// valdate that all booking_data have at least one redeemers
// validate that all booking_data have redeemers less than numPax of availability of that item
// for each item find the check in option and validate that booking_data has timeId, picupId and dropOffLocations if any
interface IValidation {
    isValid: boolean;
    message?: string;
    /**
     * Identifies the booking that failed, so the caller can send the customer
     * back to it rather than leaving them to find it. Cart item id plus the
     * traveller position within that item.
     */
    invalidItemId?: string | number;
    invalidQuantityIndex?: number;
}

export const validateCheckoutStep = (
    itemsByProducts: ICartItemsByProduct[],
    checkInOptions: IBookingCheckInOptions[],
): IValidation => {
    // Validate booking_data length matches booking_quantity for each item
    for (const product of itemsByProducts) {
        for (const fare of product.itemsByFare) {
            for (const group of fare.itemsByGroup) {
                for (const item of group.items) {
                    const numPax = getNumPax(item); // assuming you already have this helper
                    if (numPax === 1 && item.booking_data.length !== item.booking_quantity) {
                        return {
                            isValid: false,
                            message: `${product.name}: Number of booking details (${item.booking_data.length}) does not match booking quantity (${item.booking_quantity})`,
                        };
                    }

                    // Find matching check-in option for this item's fare
                    const checkInOption = checkInOptions.find(
                        (opt) =>
                            opt.fareTypeId === fare.id &&
                            opt.productPricesDetailsId === item.product_price_details_id.toString(),
                    );

                    // Validate redeemers exist and are within limits
                    for (const bookingData of item.booking_data) {
                        if (!bookingData.redeemers || bookingData.redeemers.length === 0) {
                            return {
                                isValid: false,
                                message: `${product.name}: Missing traveler information for one or more bookings`,
                                invalidItemId: item.id,
                                invalidQuantityIndex: bookingData.quantityIndex,
                            };
                        }

                        if (bookingData.redeemers.length > getNumPax(item)) {
                            return {
                                isValid: false,
                                message: `${product.name}: Number of travelers (${bookingData.redeemers.length}) exceeds available capacity (${item.availability.NumAvailable})`,
                            };
                        }

                        if (checkInOption) {
                            const hasPickupTimes =
                                checkInOption.pickupTimes.flatMap((pt) => pt.options).length > 0;
                            const hasPickupLocations =
                                checkInOption.pickupLocations.flatMap((pl) => pl.options).length >
                                0;
                            if (hasPickupTimes && !bookingData.timeId) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing booking time selection`,
                                };
                            }

                            if (hasPickupLocations && !bookingData.pickupId) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing pickup location selection`,
                                };
                            }
                            // Validate pickup time is not in the past
                            if (bookingData.pickupLocation) {
                                const isPickupTimeInPast = (
                                    displayName: string,
                                    bookingDate: string,
                                ): boolean => {
                                    let timeMatch = displayName.match(
                                        /(\d{1,2}):(\d{2})\s*(AM|PM)/i,
                                    );
                                    if (timeMatch) {
                                        const [, hours, minutes, period] = timeMatch;
                                        let hour = parseInt(hours, 10);
                                        if (period.toUpperCase() === "PM" && hour !== 12)
                                            hour += 12;
                                        if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
                                        const pickupDateTime = moment(bookingDate)
                                            .hour(hour)
                                            .minute(parseInt(minutes, 10));
                                        return pickupDateTime.isBefore(moment());
                                    }
                                    timeMatch = displayName.match(/^(\d{1,2}):(\d{2})/);
                                    if (timeMatch) {
                                        const [, hours, minutes] = timeMatch;
                                        const hour = parseInt(hours, 10);
                                        const minute = parseInt(minutes, 10);
                                        const pickupDateTime = moment(bookingDate)
                                            .hour(hour)
                                            .minute(minute);
                                        return pickupDateTime.isBefore(moment());
                                    }
                                    return false;
                                };

                                if (
                                    isPickupTimeInPast(
                                        bookingData.pickupLocation,
                                        item.booking_date,
                                    )
                                ) {
                                    return {
                                        isValid: false,
                                        message: `${product.name}: Selected pickup time is in the past. Please select a different date or time.`,
                                    };
                                }
                            }

                            if (
                                checkInOption.dropoffLocations.flatMap((d) => d.options).length >
                                    0 &&
                                !bookingData.dropoffId
                            ) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Missing drop-off location selection`,
                                };
                            }
                        }

                        for (const optionalField of item.booking_details.optionalFields) {
                            if (optionalField.mandatory) {
                                const value =
                                    bookingData.optionalData?.[optionalField.optionalFieldId];
                                const isValueMissing =
                                    value === undefined ||
                                    value === null ||
                                    (typeof value === "string" && value.trim() === "") ||
                                    (typeof value !== "number" && _.isEmpty(value));
                                if (isValueMissing) {
                                    return {
                                        isValid: false,
                                        message: `${product.name}: Missing optional data for “${optionalField.title}”`,
                                    };
                                }
                            }
                        }
                    }

                    if (checkInOption) {
                        const allTimeOpts = checkInOption.pickupTimes.flatMap((pt) => pt.options);
                        const countByTime: Record<string, number> = {};
                        for (const bd of item.booking_data) {
                            if (bd.timeId) {
                                countByTime[bd.timeId] = (countByTime[bd.timeId] ?? 0) + 1;
                            }
                        }
                        for (const [timeId, count] of Object.entries(countByTime)) {
                            const opt = allTimeOpts.find((o) => o.id === timeId);
                            if (
                                opt?.numAvailable !== undefined &&
                                opt.numAvailable !== null &&
                                count > opt.numAvailable
                            ) {
                                return {
                                    isValid: false,
                                    message: `${product.name}: Your quantity (${count}) exceeds availability (${opt.numAvailable}) for the selected time (${opt.displayName}). Please reduce your quantity or choose a different time.`,
                                };
                            }
                        }
                    }
                }
            }
        }
    }

    // If all validations pass
    return {
        isValid: true,
    };
};

const normalizePickupId = (pid: any) => (pid && pid !== "undefined" ? String(pid) : null);

const mergeBookingDataIfNeeded = (item: ICartItemData) => {
    const ppdId = String(item.product_price_details_id);
    const fares = item.product?.json?.faresprices ?? [];
    const matchingFare = fares.find((fp: any) => String(fp.productPricesDetailsId) === ppdId);
    const numPax = matchingFare ? Number(matchingFare.numPax) || 1 : 1;
    // Always normalize pickupId on every booking entry
    const normalized = item.booking_data.map((bd) => ({
        ...bd,
        pickupId: normalizePickupId(bd.pickupId),
    }));

    if (numPax <= 1) {
        // just return normalized booking_data
        return normalized;
    }

    // numPax > 1 -> merge entries by key
    const map = new Map<string, any>();

    for (const bd of normalized) {
        // group key: timeId + pickupId + commences + quantityIndex
        const key = JSON.stringify({
            timeId: bd.timeId ?? null,
            pickupId: bd.pickupId ?? null,
            commences: bd.commences ?? null,
            quantityIndex: bd.quantityIndex ?? null,
        });

        const existing = map.get(key);
        if (!existing) {
            // clone and ensure redeemers normalized to array
            map.set(key, {
                ...bd,
                redeemers: _.isArray(bd.redeemers)
                    ? [...bd.redeemers]
                    : bd.redeemers
                      ? [bd.redeemers]
                      : [],
            });
        } else {
            // append redeemers
            const newRedeemers = _.isArray(bd.redeemers)
                ? bd.redeemers
                : bd.redeemers
                  ? [bd.redeemers]
                  : [];
            existing.redeemers = existing.redeemers.concat(newRedeemers);
            // optionally keep other fields (optionalData etc.) - here we keep existing ones
        }
    }

    // dedupe redeemers in each merged entry and return array
    return _.map(Array.from(map.values()), (bd) => ({
        ...bd,
        redeemers: _.uniq(bd.redeemers),
    }));
};

export const transformCartItemsToUpdateCartItemsData = (
    items: ICartItemData[],
): IUpdateCartItemData[] => {
    return items.map((item) => ({
        cartItemId: item.id,
        quantity: item.booking_quantity,
        bookingData: mergeBookingDataIfNeeded(item).map((bd: any) => ({
            ...bd,
            pickupId: normalizePickupId(bd.pickupId),
        })),
    }));
};

export const transformCartItemToAddItemBase = (items: ICartItemData[]): IAddItemBase[] => {
    return items.map((item) => ({
        tdmsProductId: item.tdms_product_id.toString(),
        productPricesDetails: [
            {
                productPricesDetailsId: item.product_price_details_id.toString(),
                quantityDetails: [
                    {
                        quantity: item.booking_quantity,
                        bookingDate: moment(item.booking_date).format("DD-MMM-YYYY"),
                        bookingData: mergeBookingDataIfNeeded(item),
                        timeId: item.time_id?.toString(),
                    } as any,
                ],
            },
        ],
        startDate: moment(item.start_date).format("DD-MMM-YYYY"),
        days: item.days,
        selectedAvailableIndices: [item.selected_index],
        timeId: item.time_id ?? null,
    }));
};

export const transformCartItemsForQuote = (items: ICartItemData[]): IAddItemBase[] => {
    return items.map((item) => ({
        tdmsProductId: item.tdms_product_id.toString(),
        productPricesDetails: [
            {
                productPricesDetailsId: item.product_price_details_id.toString(),
                quantityDetails: [
                    {
                        quantity: item.booking_quantity,
                        bookingDate: moment(item.booking_date).format("DD-MMM-YYYY"),
                        timeId: item.time_id?.toString() ?? "",
                    },
                ],
            },
        ],
        startDate: moment(item.start_date).format("DD-MMM-YYYY"),
        days: item.days,
        selectedAvailableIndices: [item.selected_index],
    }));
};

/**
 * Update the items in the cart or quote
 * @param items - the items to update
 * @param mode - the mode to update the items
 * @param referenceId - pass sessionId for cart and quoteId for quote
 * @returns the response from the api call
 */
export const updateItems = async (
    items: IUpdateCartItemData[],
    mode: "quote" | "cart",
    referenceId?: string,
) => {
    switch (mode) {
        case "cart": {
            const saveCartItemsResponse = await cartApi.updateCartItems(items, referenceId); // send the whole array
            return saveCartItemsResponse.data; // assuming .data is the array of responses
        }
        case "quote": {
            const response = await quoteApi.updateQuoteItems(items, Number(referenceId));
            return response.data;
        }
    }
};

/**
 * Build optional_fields object from optionalData.
 *
 * Keys by the field's `commonName` when present. Some products (e.g. TDMS product
 * 27815's mandatory weight field) return `commonName: null`, in which case we fall
 * back to the field's `optionalFieldId`. Without this fallback the value would be
 * written under a literal `"null"` key, which the backend cannot match to the
 * field — producing a false "you have missed entering a weight for redeemer" error.
 *
 * @param optionalData - the optionalData from booking_data (keyed by fieldId)
 * @param optionalFields - the optional field definitions containing commonName mapping
 * @returns object keyed by commonName (or optionalFieldId as fallback) with their values
 */
export const buildOptionalFieldsPayload = (
    optionalData: Record<string, any> | undefined,
    optionalFields: IOptionalField[],
): Record<string, any> => {
    if (!optionalData || !optionalFields) return {};

    const result: Record<string, any> = {};
    for (const field of optionalFields) {
        const value = optionalData[field.optionalFieldId];
        if (value === undefined || value === null || value === "") continue;

        const commonName =
            field.commonName !== null && field.commonName !== undefined
                ? String(field.commonName).trim()
                : "";
        const key = commonName || String(field.optionalFieldId ?? "").trim();
        if (!key) continue;

        result[key] = value;
    }
    return result;
};

export const buildBookingDataForNewQuantity = (
    quantityIndex: number,
    timeId: string,
): BookingData => ({
    timeId,
    quantityIndex,
    commences: null,
    pickupId: null,
    optionalData: [],
});
export const buildItemsForNewQuantity = (
    items: ICartItemData[],
    newQuantity: number,
    originalItems: ICartItemData[],
): ICartItemData[] => {
    if (newQuantity < 1) {
        throw new Error("New quantity must be greater than 0");
    }

    return items.map((item) => ({
        ...item,
        booking_quantity: newQuantity,
        booking_data:
            newQuantity < item.booking_quantity
                ? item.booking_data.slice(0, newQuantity) // remove excess booking data
                : [
                      ...item.booking_data,
                      ..._.range(item.booking_quantity, newQuantity).map((newQuantityIndex) => {
                          const quantityIndex = newQuantityIndex + 1;
                          const originalItem = originalItems.find((oi) => oi.id === item.id);
                          const existingBookingData = originalItem?.booking_data?.[quantityIndex]; // populate booking data from original item
                          return (
                              existingBookingData ||
                              buildBookingDataForNewQuantity(
                                  quantityIndex,
                                  item.booking_data[0].timeId,
                              )
                          );
                      }),
                  ],
    }));
};

/**
 * Parse date time in the time zone but returns date and time in local time zone
 * This is useful for date time comparison in the local time zone
 * Example: 2025-10-01T00:00:00+10:00 is Oct 1 in AEST but Sep 30 of NPT so returned date is Oct 1 in NPT
 * @param date - date in format "2025-06-30T10:00:00Z"
 * @returns date in format "2025-06-30T10:00:00"
 */
export const stripTimeZone = (date: string) => {
    const originalTravelDate = moment.parseZone(date);
    return moment({
        year: originalTravelDate.year(),
        month: originalTravelDate.month(), // zero-indexed in Moment.js
        day: originalTravelDate.date(),
        hour: originalTravelDate.hour(),
        minute: originalTravelDate.minute(),
        second: originalTravelDate.second(),
    });
};
